/**
 * Shared reading of the ArgoCD Application API.
 *
 * Two screens consume the very same payload: the environments card
 * (`OdooEnvironmentsCard`), which lists one Application per branch of a
 * project, and the per-branch page (`BranchPage`), which opens exactly one of
 * them. The Application shape, the mapping onto an environment, the status
 * vocabulary and the authenticated call itself therefore live here rather than
 * being duplicated on both sides and drifting apart — the same reasoning that
 * put the resource-tree reading in `components/argocd/pods.ts`.
 *
 * Everything below is a plain function: no hook, no translation ref, no config
 * lookup. That is what lets the tests call it directly, and it is why the two
 * label helpers take a `t` function as an argument instead of reaching for one.
 */

import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import type { OpenIdConnectApi } from '@backstage/core-plugin-api';

/**
 * ArgoCD health and sync statuses to message keys.
 *
 * These two cannot be checked against a union the way the CI statuses can: the
 * CI status is normalised by our own backend into a closed set, while these
 * arrive verbatim from the ArgoCD API as free-form strings. The lists below are
 * ArgoCD's documented values, but nothing guarantees they stay exhaustive —
 * hence the fallback in {@link healthLabel} and {@link syncLabel}.
 */
export const HEALTH_LABEL_KEYS = {
  Healthy: 'health.healthy',
  Progressing: 'health.progressing',
  Degraded: 'health.degraded',
  Suspended: 'health.suspended',
  Missing: 'health.missing',
  Unknown: 'health.unknown',
} as const;

export const SYNC_LABEL_KEYS = {
  Synced: 'sync.synced',
  OutOfSync: 'sync.outOfSync',
  Unknown: 'sync.unknown',
} as const;

/** One entry of `status.history`, as ArgoCD sends it. */
export interface ArgoRevisionHistory {
  id?: number;
  revision?: string;
  deployedAt?: string;
  deployStartedAt?: string;
  source?: { repoURL?: string; targetRevision?: string };
}

/**
 * The subset of an ArgoCD Application these screens read. The API returns a lot
 * more; only what we consume is declared.
 *
 * Every field below `metadata.name` is optional because the list endpoint and
 * the single-Application endpoint do not return the same depth of status, and
 * because an Application that has never synced carries neither history nor
 * revision.
 */
export interface ArgoApplication {
  metadata: {
    name: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec?: {
    source?: { targetRevision?: string; repoURL?: string };
    destination?: { namespace?: string };
  };
  status?: {
    sync?: { status?: string; revision?: string };
    health?: { status?: string };
    history?: ArgoRevisionHistory[] | null;
  };
}

export interface OdooEnvironment {
  appName: string;
  branch: string;
  syncStatus: string;
  healthStatus: string;
  url?: string;
  /** Namespace the branch is deployed into — the Grafana dashboard filter. */
  namespace?: string;
  /**
   * Browsable URL of the project on its forge, i.e. the clone URL without
   * `.git`. Its host is what decides whether the CI status is read from GitLab
   * pipelines or GitHub Actions runs.
   */
  repoUrl?: string;
}

/**
 * Maps an ArgoCD health status onto the semantic colours of Backstage UI.
 *
 * ArgoCD's health model has exactly six statuses — Healthy, Progressing,
 * Degraded, Suspended, Missing, Unknown — and this switch is written against
 * that list, not against a guess. `Degraded` is a failure state there, the one
 * ArgoCD paints red in its own UI, so it belongs with `danger`; it sat in
 * `warning` next to `Progressing`, which understated a broken environment as
 * "still settling". `Suspended` and `Unknown` fall through to `secondary`,
 * which is what neither-good-nor-bad should look like.
 *
 * The `status` parameter stays a plain string because the value arrives
 * verbatim from the ArgoCD API: an upgrade that adds a seventh status must land
 * in `default`, not fail to compile.
 */
export function healthColor(
  status: string,
): 'success' | 'warning' | 'danger' | 'secondary' {
  switch (status) {
    case 'Healthy':
      return 'success';
    case 'Progressing':
      return 'warning';
    case 'Degraded':
    case 'Missing':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function toEnvironment(app: ArgoApplication): OdooEnvironment {
  const repoURL = app.spec?.source?.repoURL;

  return {
    appName: app.metadata.name,
    // The label is set by the applicationset; targetRevision is the fallback
    // should the label ever go missing.
    branch:
      app.metadata.labels?.['app-branch'] ??
      app.spec?.source?.targetRevision ??
      '?',
    syncStatus: app.status?.sync?.status ?? 'Unknown',
    healthStatus: app.status?.health?.status ?? 'Unknown',
    url: app.metadata.annotations?.['odoo/url'],
    // Read from the Application rather than rebuilt from a naming convention:
    // the applicationset owns both, and reading them keeps the links correct if
    // the convention ever changes.
    namespace: app.spec?.destination?.namespace,
    repoUrl: repoURL?.replace(/\.git$/, ''),
  };
}

/**
 * The Application of one branch, out of everything the `odoo-project` selector
 * returned.
 *
 * Matched on the *data* and never on the name: the Application name is composed
 * by the applicationset, so rebuilding `odoo-<project>-<branch>` here would
 * break the day that template changes — the same shortcut {@link toEnvironment}
 * refuses to take for the namespace and the repository URL.
 *
 * The label-then-targetRevision fallback is deliberately identical to the one
 * in {@link toEnvironment}: the card names the row and the page looks it up, so
 * the two must answer "which branch is this?" the same way or a link built by
 * the card would land on a page that finds nothing.
 */
export function findApplicationForBranch(
  apps: ArgoApplication[],
  branch: string,
): ArgoApplication | undefined {
  return apps.find(
    app =>
      (app.metadata.labels?.['app-branch'] ??
        app.spec?.source?.targetRevision) === branch,
  );
}

/** One entry of the per-branch page's branch switcher. */
export interface BranchOption {
  branch: string;
  /** ArgoCD's health status, verbatim — what {@link healthColor} paints. */
  health: string;
  appName: string;
}

/**
 * Every branch of a project, as the switcher lists them, out of the same
 * project-wide Application list {@link findApplicationForBranch} searches.
 *
 * Built on {@link toEnvironment} rather than reading the labels again: "which
 * branch is this Application?" is a question that must have exactly one answer
 * in this module, or a switcher entry would point at a page that finds nothing.
 *
 * Sorted with a plain string comparison and not `localeCompare`: the order has
 * to be the same for every reader and in every test run, and the collation of
 * `localeCompare` depends on the environment's locale.
 */
export function branchOptions(apps: ArgoApplication[]): BranchOption[] {
  return apps
    .map(toEnvironment)
    .filter(
      // '?' is what toEnvironment answers when neither the label nor
      // targetRevision named a branch; '' is a label present but empty. Both
      // would produce a nameless row linking to a page that cannot load, so
      // they are dropped rather than rendered.
      env => env.branch !== '' && env.branch !== '?',
    )
    .map(env => ({
      branch: env.branch,
      health: env.healthStatus,
      appName: env.appName,
    }))
    .sort((a, b) => {
      if (a.branch === b.branch) {
        return 0;
      }
      return a.branch < b.branch ? -1 : 1;
    });
}

/** Every message key {@link healthLabel} and {@link syncLabel} can ask for. */
export type ArgoStatusMessageKey =
  | (typeof HEALTH_LABEL_KEYS)[keyof typeof HEALTH_LABEL_KEYS]
  | (typeof SYNC_LABEL_KEYS)[keyof typeof SYNC_LABEL_KEYS];

/**
 * Just enough of a translation function for the two lookups below. Taking it as
 * an argument, rather than calling `useTranslationRef` in this module, is what
 * keeps these helpers callable from a test and shareable between two screens.
 *
 * Callers pass `key => t(key)` and not `t` itself, on purpose: the translation
 * function is generic over *every* key of the ref, some of which take mandatory
 * interpolation values, so TypeScript widens its options parameter to a
 * required one and refuses the assignment. Wrapping it in a lambda instantiates
 * the generic on the status keys alone, where the options are optional — and it
 * does so with the compiler still checking, which a cast would not.
 */
export type ArgoStatusTranslator = (key: ArgoStatusMessageKey) => string;

/**
 * Both fall back to the raw ArgoCD value, and that fallback is the point: these
 * strings come straight from the ArgoCD API, so an upgrade that introduces a
 * status must degrade to showing it in English rather than to an empty cell. A
 * blank status would read as "no data" when the truth is "a status we have no
 * word for yet".
 *
 * The `in` check is what makes the index cast honest — it is checked on the
 * line before, not assumed.
 */
export function healthLabel(status: string, t: ArgoStatusTranslator): string {
  return status in HEALTH_LABEL_KEYS
    ? t(HEALTH_LABEL_KEYS[status as keyof typeof HEALTH_LABEL_KEYS])
    : status;
}

export function syncLabel(status: string, t: ArgoStatusTranslator): string {
  return status in SYNC_LABEL_KEYS
    ? t(SYNC_LABEL_KEYS[status as keyof typeof SYNC_LABEL_KEYS])
    : status;
}

export interface ArgoRequestOptions {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
  /** Narrowed to the one method used, so a test can pass a two-line stub. */
  autheliaAuth: Pick<OpenIdConnectApi, 'getIdToken'>;
  /**
   * Path under the `/argocd/api` proxy route, already encoded — for instance
   * `applications/odoo-app1-dev-andreas/resource-tree`.
   */
  path: string;
  /** Translated "no Authelia session" message; this module holds no strings. */
  noIdentityMessage: string;
}

/**
 * One authenticated GET against the ArgoCD proxy, returning the raw Response.
 *
 * Raw, because not every ArgoCD endpoint answers with JSON: the pod logs are
 * newline-delimited JSON and are read as text. Callers that do want JSON go
 * through {@link fetchArgoJson}.
 */
export async function argoApiFetch(
  options: ArgoRequestOptions,
): Promise<Response> {
  const { fetchApi, discoveryApi, autheliaAuth, path, noIdentityMessage } =
    options;

  // Absolute backend URL: in development the frontend runs on a different port,
  // so a relative path would hit the frontend and return the index page (HTML)
  // instead of the API answer.
  const proxyUrl = await discoveryApi.getBaseUrl('proxy');

  // The proxy carries no shared ArgoCD token: the request goes out with the
  // *user's* Authelia id_token, not the Backstage one, so ArgoCD applies its
  // own RBAC to whoever is signed in.
  //
  // optional: true because these calls run on mount — a non-optional call would
  // start an interactive sign-in, and browsers block popups that are not
  // user-initiated. Without a session we get '' and throw the message below.
  const idToken = await autheliaAuth.getIdToken({ optional: true });
  if (!idToken) {
    throw new Error(noIdentityMessage);
  }

  return fetchApi.fetch(`${proxyUrl}/argocd/api/${path}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
}

export interface ArgoJsonRequestOptions extends ArgoRequestOptions {
  /**
   * Translated message for a body that is not JSON, built from its first bytes.
   * A function and not a string because the body is only known here.
   */
  notJsonMessage: (body: string) => string;
  /**
   * Translated message to raise instead of the generic HTTP one when ArgoCD
   * answers 404.
   *
   * Optional, and only worth passing when the caller knows what a missing
   * resource means to the user. On a single-Application read it means the
   * branch is gone, which deserves a sentence; on a resource-tree read it is
   * just one more failure, and the generic message is the honest answer.
   */
  notFoundMessage?: string;
}

/**
 * {@link argoApiFetch} plus the status check and the parsing every JSON
 * endpoint needs.
 *
 * The body is read as text *before* being parsed on purpose: an ingress or a
 * proxy answering with an HTML error page has to produce a message that says
 * so, not a bare "Unexpected token <".
 */
export async function fetchArgoJson<T>(
  options: ArgoJsonRequestOptions,
): Promise<T> {
  const response = await argoApiFetch(options);
  const body = await response.text();

  if (!response.ok) {
    if (response.status === 404 && options.notFoundMessage) {
      throw new Error(options.notFoundMessage);
    }
    throw new Error(
      `ArgoCD API: HTTP ${response.status}${
        body ? ` — ${body.slice(0, 200)}` : ''
      }`,
    );
  }

  try {
    return JSON.parse(body) as T;
  } catch {
    throw new Error(options.notJsonMessage(body.slice(0, 120)));
  }
}
